import { useCallback, useEffect, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@antimatterhq/ui/components/dialog';
import { Input } from '@antimatterhq/ui/components/input';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { useUpdateRiskScore } from '@/hooks/dbui/useUpdateRiskScore';

export type ModifyRiskScoreDialogProps = {
  kind: string;
  value: string;
  riskScore: number;
  isOpen: boolean;
  onCancel: () => void;
  onSave: (riskScore: number) => void;
};

export default function ModifyRiskScoreDialog({
  kind,
  value,
  riskScore: initialRiskScore,
  isOpen,
  onSave,
  onCancel,
}: ModifyRiskScoreDialogProps) {
  const [riskScore, setRiskScore] = useState<number | undefined>(
    initialRiskScore,
  );
  useEffect(() => {
    setRiskScore(initialRiskScore);
  }, [initialRiskScore]);

  const { mutateAsync, isPending } = useUpdateRiskScore({ value, kind });

  const onSetRiskScore = useCallback(async () => {
    if (riskScore === undefined) {
      return;
    }

    await mutateAsync(
      {
        setTo: riskScore,
      },
      {
        onSuccess: async () => {
          onSave(riskScore);
        },
        toasts: {
          success: 'Risk score updated',
          error: 'Failed to update risk score',
        },
      },
    );
  }, [riskScore, mutateAsync, onSave]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modify Risk Score</DialogTitle>
        </DialogHeader>
        <div>
          <Button
            className="w-full"
            onClick={() => setRiskScore(0)}
            variant="primary"
          >
            Set Risk to 0
          </Button>

          <Spacer size="lg" />

          <Text className="block text-center">or</Text>

          <Spacer size="base" />

          <Label htmlFor="riskScore" id="riskScore-label">
            New Risk Score Value*
          </Label>
          <Input
            id="riskScore"
            name="riskScore"
            value={riskScore === undefined ? '' : riskScore}
            onChange={(e) =>
              setRiskScore(
                e.target.value === '' ? undefined : Number(e.target.value),
              )
            }
            autoComplete="off"
            disabled={isPending || !isOpen}
            aria-labelledby="riskScore-label"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            onClick={onSetRiskScore}
            variant="primary"
            disabled={riskScore === undefined || isPending || !isOpen}
            loading={isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
